import { Injectable } from '@angular/core';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import {
    ComponentType,
    ComponentsOptionsDTO,
    IEquityOptions,
    IFinancialLineItemOptions,
    IFreeTableOptions,
    IMovementScheduleEditableColumnHeaders,
    IRichText,
    ISelectOptions,
    Options,
} from '@modeso/types__aumico-lib-notes-be';
import { OptionsKeys } from '../enums/componentOptionsKeys.enum';
import { MovementScheduleColumnIndexes } from '../enums/movementScheduleColumnIndexes.enum';
import { IComponentOptionsProperties } from '../interfaces/IComponentOptionsProperties.interface';
import { DialogTypes } from '../types/dialog.types';

const SINGLE_ELEMENT_COMPONENT_OPTIONS = 0;

@Injectable({
    providedIn: 'root',
})
export class ComponentOptionsKeys {
    // TODO: read from env
    languages: string[] = ['de-ch', 'en-us'];

    constructor() {}

    getKeysByComponentName(
        componentName: ComponentType,
        languages: string[],
        componentsOptions: Map<string, Omit<ComponentsOptionsDTO, 'componentName'>>,
    ): DialogTypes | undefined {
        this.languages = languages;

        const componentData = this.getComponentOptionsDataByComponentName(componentName, componentsOptions);

        switch (componentName) {
            case ComponentType.EQUITY:
                const equityComponentOption =
                    componentData != undefined
                        ? (componentData[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IEquityOptions)
                        : null;
                return this.getEquityKeys(equityComponentOption);

            case ComponentType.FINANCIAL_LINE_ITEM:
                const financialLineItemComponentOption =
                    componentData != undefined
                        ? (componentData[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IFinancialLineItemOptions)
                        : null;
                return this.getFinancialLineItemKeys(financialLineItemComponentOption);

            case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                const freeTableWithAmountComponentOption =
                    componentData != undefined
                        ? (componentData[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IFreeTableOptions)
                        : null;
                return this.getFreeTableWithAmountsKeys(freeTableWithAmountComponentOption);

            case ComponentType.RICH_TEXT:
                const richTextComponentOption = componentData as IRichText[];
                return this.getRichTextKeys(languages, richTextComponentOption);

            case ComponentType.SELECT:
                const selectComponentOption =
                    componentData != undefined
                        ? (componentData[SINGLE_ELEMENT_COMPONENT_OPTIONS] as ISelectOptions)
                        : null;
                return this.getSelectKeys(selectComponentOption);

            case ComponentType.INPUT:
                return undefined;

            case ComponentType.MOVEMENT_SCHEDULE:
                const movementScheduleComponentOption =
                    componentData != undefined
                        ? (componentData[SINGLE_ELEMENT_COMPONENT_OPTIONS] as IMovementScheduleEditableColumnHeaders)
                        : null;
                return this.getMovementScheduleKeys(movementScheduleComponentOption);

            case ComponentType.SUPER_TABLE:
                return undefined;

            default:
                throw new Error(` This component: ${componentName} is not supported!`);
        }
    }

    private getComponentOptionsDataByComponentName(
        componentName: string,
        componentsOptions: Map<string, Omit<ComponentsOptionsDTO, 'componentName'>>,
    ): Options[] | null {
        if (componentsOptions.get(componentName) == null) {
            return null;
        }

        return componentsOptions.get(componentName).componentOptions;
    }

    private getRichTextKeys(languages: string[], data: IRichText[]): IRichText[] {
        const richTextProperties: IRichText[] = [];

        languages.forEach((language: string) => {
            const richText: IRichText = {
                language,
                text: data != null ? data.find((element) => element.language === language).text : null,
            };

            richTextProperties.push(richText);
        });
        return richTextProperties;
    }

    private getEquityKeys(data: IEquityOptions): IComponentOptionsProperties {
        const obj: IComponentOptionsProperties = {
            properties: [
                {
                    name: OptionsKeys.Total_Row,
                    dynamic: false,
                    identifier: 'totalRow',
                    property: 'totalRow',
                    toggle: true,
                    required: false,
                    value: this.getDataOfObjectByKey(data, 'totalRow'),
                },

                {
                    name: OptionsKeys.Text_Title,
                    dynamic: false,
                    identifier: 'textTitle',
                    property: this.generatePropertyTranslations('textTitle'),
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'textTitle'),
                },
                {
                    name: OptionsKeys.Current_Value_Title_1,
                    dynamic: false,
                    identifier: 'currentValueTitle1',
                    property: this.generatePropertyTranslations('currentValueTitle1'),
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'currentValueTitle1'),
                },
                {
                    name: OptionsKeys.Current_Value_Title_2,
                    dynamic: false,
                    identifier: 'currentValueTitle2',
                    property: this.generatePropertyTranslations('currentValueTitle2'),
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'currentValueTitle2'),
                },
                {
                    name: OptionsKeys.Previous_Value_Title_1,
                    dynamic: false,
                    identifier: 'previousValueTitle1',
                    property: this.generatePropertyTranslations('previousValueTitle1'),
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'previousValueTitle1'),
                },
                {
                    name: OptionsKeys.Previous_Value_Title_2,
                    dynamic: false,
                    identifier: 'previousValueTitle2',
                    property: this.generatePropertyTranslations('previousValueTitle2'),
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'previousValueTitle2'),
                },
            ],
        };

        return obj;
    }

    private getFinancialLineItemKeys(data: IFinancialLineItemOptions): IComponentOptionsProperties {
        const obj: IComponentOptionsProperties = {
            properties: [
                {
                    name: OptionsKeys.Title,
                    property: this.generatePropertyTranslations('text'),
                    identifier: 'text',
                    dynamic: false,
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'text'),
                },
            ],
        };

        return obj;
    }

    private getSelectKeys(data: ISelectOptions): IComponentOptionsProperties {
        const obj: IComponentOptionsProperties = {
            properties: [
                {
                    name: OptionsKeys.Title,
                    property: this.generatePropertyTranslations('title'),
                    identifier: 'title',
                    dynamic: false,
                    required: false,
                    value: this.getDataOfObjectByKey(data, 'title'),
                },
                {
                    name: OptionsKeys.Text,
                    property: this.generatePropertyTranslations('text'),
                    identifier: 'text',
                    dynamic: false,
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'text'),
                },

                {
                    name: OptionsKeys.Selection_Option,
                    dynamic: true,
                    identifier: 'selectionOption',
                    property: this.generatePropertyTranslations('selectionOption'),
                    required: true,
                    value: this.getDataOfObjectByKey(data, 'selectionOption'),
                },
            ],
        };
        return obj;
    }

    private getFreeTableWithAmountsKeys(data: IFreeTableOptions): IComponentOptionsProperties {
        const obj: IComponentOptionsProperties = {
            properties: [
                {
                    name: OptionsKeys.Total_Row,
                    property: 'totalRow',
                    identifier: 'totalRow',
                    toggle: true,
                    dynamic: false,
                    required: false,
                    value: this.getDataOfObjectByKey(data, 'totalRow'),
                },
                {
                    name: OptionsKeys.Title,
                    property: this.generatePropertyTranslations('title'),
                    identifier: 'title',
                    dynamic: false,
                    required: false,
                    value: this.getDataOfObjectByKey(data, 'title'),
                },
            ],
        };

        return obj;
    }

    private getDataOfObjectByKey(data: Options, key: string): boolean | IDynamicKeyValue | IDynamicKeyValue[] {
        if (data == null) {
            return null;
        }

        return data[key];
    }

    private generatePropertyTranslations(primaryPropertyName: string) {
        const translatedProperty: IDynamicKeyValue = {};
        this.languages.forEach((language) => {
            // the following line will produce for example title1-en-us
            const propertyName = primaryPropertyName.concat('-' + language);
            translatedProperty[language] = propertyName;
        });
        return translatedProperty;
    }

    private getMovementScheduleKeys(data: IMovementScheduleEditableColumnHeaders): IComponentOptionsProperties {
        return {
            properties: [
                {
                    name: OptionsKeys.Title,
                    property: this.generatePropertyTranslations('title'),
                    identifier: 'title',
                    dynamic: false,
                    required: false,
                    value: this.getDataOfObjectByKey(data, 'title'),
                },
                {
                    name: OptionsKeys.Movements_Schedule_Column1,
                    property: this.generatePropertyTranslations(MovementScheduleColumnIndexes.FIRST_COLUMN),
                    identifier: MovementScheduleColumnIndexes.FIRST_COLUMN,
                    dynamic: false,
                    required: true,
                    value: this.getDataOfObjectByKey(data, MovementScheduleColumnIndexes.FIRST_COLUMN),
                },
                {
                    name: OptionsKeys.Movements_Schedule_Column2,
                    property: this.generatePropertyTranslations(MovementScheduleColumnIndexes.SECOND_COLUMN),
                    identifier: MovementScheduleColumnIndexes.SECOND_COLUMN,
                    dynamic: false,
                    required: true,
                    value: this.getDataOfObjectByKey(data, MovementScheduleColumnIndexes.SECOND_COLUMN),
                },
                {
                    name: OptionsKeys.Movements_Schedule_Column3,
                    property: this.generatePropertyTranslations(MovementScheduleColumnIndexes.THIRD_COLUMN),
                    identifier: MovementScheduleColumnIndexes.THIRD_COLUMN,
                    dynamic: false,
                    required: true,
                    value: this.getDataOfObjectByKey(data, MovementScheduleColumnIndexes.THIRD_COLUMN),
                },
                {
                    name: OptionsKeys.Movements_Schedule_Column4,
                    property: this.generatePropertyTranslations(MovementScheduleColumnIndexes.FORTH_COLUMN),
                    identifier: MovementScheduleColumnIndexes.FORTH_COLUMN,
                    dynamic: false,
                    required: true,
                    value: this.getDataOfObjectByKey(data, MovementScheduleColumnIndexes.FORTH_COLUMN),
                },
            ],
        };
    }
}
